// Fonts are provided by design-studio via `studioFontClass` on <html> in
// `src/app/layout.tsx`, which exposes `--font-display-app` (Space Grotesk),
// `--font-sans-app` (Inter), `--font-mono-app` (IBM Plex Mono). DS's globals
// (`@import 'design-studio/globals.css'`) maps these into the standard
// Tailwind utilities `font-display`, `font-sans`, `font-mono`. Pass those as
// `fontFamily` here — e.g. `<TextH1 fontFamily="font-display">`.
import { Join } from 'design-studio/lib'

interface TextH1Props {
  children: React.ReactNode
  fontFamily?: string
  fontStyle?: string
  colors?: string
  className?: string
  tracking?: string
}

export function TextH1({
  children,
  fontFamily,
  fontStyle = 'text-5xl sm:text-6xl font-semibold dark:font-normal pb-2',
  colors = 'text-indigo-950 dark:text-white',
  className,
  tracking = 'tracking-tight',
}: TextH1Props) {
  const Classes = Join(
    '',
    'text-left',
    fontStyle,
    tracking,
    fontFamily,
    colors,
    className,
  )

  return <h1 className={Classes}>{children}</h1>
}

interface TextPreTitleProps {
  children: React.ReactNode
  colors?: string // Optional colors prop, defaults to 'text-blue-500'
  className?: string // Optional additional classes
}

export function TextPreTitle({
  children,
  colors = 'text-blue-500',
  className,
}: TextPreTitleProps) {
  const Classes = Join('text-sm font-semibold', colors, className)

  return <p className={Classes}>{children}</p>
}

interface TextH2Props {
  children: React.ReactNode
  fontFamily?: string // Optional fontFamily prop
  colors?: string // Optional colors prop, defaults to 'text-blue-900 dark:text-white'
  text?: string // Optional text prop, defaults to 'font-normal text-4xl sm:text-5xl leading-snug'
  className?: string // Optional additional classes
}

export function TextH2({
  children,
  fontFamily,
  text = 'font-normal text-4xl sm:text-5xl leading-snug',
  colors = 'text-slate-950 dark:text-white',
  className,
}: TextH2Props) {
  const Classes = Join(text, fontFamily, colors, className)

  return <h2 className={Classes}>{children}</h2>
}

interface TextH3Props {
  children: React.ReactNode
  fontFamily?: string // Optional fontFamily prop
  text?: string // Optional text prop, defaults to 'text-2xl sm:text-3xl leading-snug font-light'
  colors?: string // Optional colors prop, defaults to 'text-blue-900 dark:text-white'
  className?: string // Optional additional classes
}

export function TextH3({
  children,
  fontFamily,
  text = 'text-2xl sm:text-3xl leading-snug font-light',
  colors = 'text-slate-950 dark:text-white',
  className,
}: TextH3Props) {
  const Classes = Join(text, fontFamily, colors, className)

  return <h3 className={Classes}>{children}</h3>
}

interface TextH1SubtitleProps {
  children: React.ReactNode
  colors?: string // Optional colors prop, defaults to 'text-slate-700 dark:text-slate-300'
  className?: string // Optional additional classes
  fontFamily?: string // Optional fontFamily prop
}

export function TextH1Subtitle({
  children,
  colors = 'text-slate-700 dark:text-slate-300',
  className,
  fontFamily = 'font-mono',
}: TextH1SubtitleProps) {
  const Classes = Join(
    'mt-3',
    'text-left text-pretty',
    'text-2xl',
    colors,
    className,
    fontFamily,
  )

  return <p className={Classes}>{children}</p>
}

interface TextH2SubtitleProps {
  children: React.ReactNode
  colors?: string // Optional colors prop, defaults to 'text-slate-600 dark:text-slate-300'
  className?: string // Optional additional classes
}

export function TextH2Subtitle({
  children,
  colors = 'text-slate-600 dark:text-slate-300',
  className,
}: TextH2SubtitleProps) {
  const Classes = Join(
    'mx-auto',
    'mt-4',
    'text-xl',
    'font-mono',
    colors,
    className,
  )

  return <p className={Classes}>{children}</p>
}

interface TextParagraphProps {
  children: React.ReactNode
  colors?: string // Optional colors prop, defaults to 'text-slate-600 dark:text-slate-300/80'
  text?: string // Optional size prop, defaults to 'text-base font-light leading-relaxed'
  /** Font family Tailwind utility. Defaults to `font-display` (Space
   *  Grotesk) — these are SectionHeader-tier subtitles/descriptions, not
   *  body copy. Pass `font-sans` to flip to Inter for dense paragraphs. */
  fontFamily?: string
  className?: string // Optional additional classes
}

export function TextParagraph({
  children,
  colors = 'text-slate-600 dark:text-slate-300/80',
  text = 'text-base font-light leading-relaxed',
  fontFamily = 'font-display',
  className,
}: TextParagraphProps) {
  const Classes = Join('mt-3 lg:mt-4', fontFamily, text, colors, className)

  return <p className={Classes}>{children}</p>
}
